// Knowledge Base — single-database list view.
//
// Reuses the same component for all 7 KB databases. The schema (fields,
// label, icon, primary field) comes from the backend GET /api/kb/databases
// + the kbConfig.js client-side lookup for icons/colours.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Plus,
  Search,
  Tag,
  Sparkles,
  Loader2,
  TrendingUp,
  Brain,
  Wand2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DB_BY_KEY } from "./kbConfig";

function chipForStatus(value) {
  const v = (value || "").toString().toLowerCase();
  if (["active", "approved", "live"].includes(v)) return "bg-emerald-500/15 text-emerald-300";
  if (["pilot", "vetting", "in review", "interviewed"].includes(v)) return "bg-amber-500/15 text-amber-300";
  if (["draft", "research"].includes(v)) return "bg-slate-500/15 text-slate-300";
  if (["retired", "rejected", "deprecated", "churned"].includes(v)) return "bg-rose-500/15 text-rose-300";
  return "bg-slate-500/15 text-slate-300";
}

function tagStr(v) {
  if (!v) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

export default function AdminKnowledgeBaseDatabase() {
  const { dbKey } = useParams();
  const navigate = useNavigate();
  const dbMeta = DB_BY_KEY[dbKey];

  const [schema, setSchema] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTrends, setAiTrends] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Generate-from-name modal state
  const [genOpen, setGenOpen] = useState(false);
  const [genHint, setGenHint] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  // Load schema + records.
  useEffect(() => {
    if (!dbMeta) {
      toast.error("Unknown database");
      navigate("/admin/knowledge-base");
      return;
    }
    setLoading(true);
    Promise.all([
      api.get("/kb/databases"),
      api.get(`/kb/${dbKey}`, { params: { q: q || undefined, tag: tag || undefined } }),
    ])
      .then(([r1, r2]) => {
        const s = (r1.data || []).find((d) => d.key === dbKey);
        setSchema(s);
        setRecords(r2.data || []);
      })
      .catch((e) => toast.error(formatApiError(e, "Failed to load")))
      .finally(() => setLoading(false));
  }, [dbKey, dbMeta, navigate, q, tag]);

  const allTags = useMemo(() => {
    const t = new Set();
    records.forEach((r) => (r.tags || []).forEach((x) => t.add(x)));
    return Array.from(t).sort();
  }, [records]);

  if (!dbMeta) return null;
  const Icon = dbMeta.icon;
  const cls = dbMeta.cls;

  const onCreate = async () => {
    try {
      const body = { [dbMeta.primary]: "Untitled record" };
      const r = await api.post(`/kb/${dbKey}`, body);
      navigate(`/admin/knowledge-base/${dbKey}/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e, "Could not create record"));
    }
  };

  const onExport = () => {
    // Programmatic auth needed — use fetch w/ token then trigger download
    const token = localStorage.getItem("nodera_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kb/${dbKey}/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `kb-${dbKey}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      })
      .catch((e) => toast.error(`Export failed: ${e.message}`));
  };

  const runTrends = async () => {
    setAiLoading(true);
    setAiOpen(true);
    setAiTrends(null);
    try {
      const r = await api.post("/kb/ai/trends", { db_key: dbKey });
      setAiTrends(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "AI trends failed"));
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const runGenerate = async () => {
    const hint = genHint.trim();
    if (!hint) return;
    setGenBusy(true);
    try {
      const r = await api.post("/kb/ai/generate-record", {
        db_key: dbKey,
        hint,
        save: true,
      });
      toast.success(`Generated "${r.data.record?.[dbMeta.primary] || hint}"`);
      setGenOpen(false);
      setGenHint("");
      navigate(`/admin/knowledge-base/${dbKey}/${r.data.record.id}`);
    } catch (e) {
      toast.error(formatApiError(e, "AI generation failed"));
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div
      data-testid={`kb-db-page-${dbKey}`}
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 space-y-6"
    >
      {/* Breadcrumb */}
      <Link
        to="/admin/knowledge-base"
        data-testid="kb-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="h-3 w-3" /> Knowledge Base
      </Link>

      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className={`text-[10.5px] uppercase tracking-[0.16em] font-semibold ${cls.text} flex items-center gap-1.5`}>
            <Icon className="h-3 w-3" /> {dbMeta.label}
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-100">
            {records.length} record{records.length === 1 ? "" : "s"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">{dbMeta.description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="kb-trends-btn"
            variant="outline"
            onClick={runTrends}
            disabled={aiLoading || records.length === 0}
            className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
          >
            {aiLoading ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <TrendingUp className="mr-1 h-3.5 w-3.5" />
            )}
            AI Trends
          </Button>
          <Button
            data-testid="kb-export-btn"
            variant="outline"
            onClick={onExport}
            className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
          >
            <Download className="mr-1 h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button
            data-testid="kb-generate-btn"
            onClick={() => setGenOpen(true)}
            variant="outline"
            className="border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
          >
            <Wand2 className="mr-1 h-3.5 w-3.5" /> Generate from name
          </Button>
          <Button data-testid="kb-create-btn" onClick={onCreate}>
            <Plus className="mr-1 h-3.5 w-3.5" /> New record
          </Button>
        </div>
      </header>

      {/* Filters */}
      <section className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="kb-db-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${dbMeta.label.toLowerCase()}…`}
            className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500"
          />
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Tag className="h-3 w-3 text-slate-400" />
            <button
              onClick={() => setTag("")}
              className={`rounded-full px-2 py-0.5 text-[11px] ${
                !tag ? "bg-slate-100 text-slate-900" : "border border-white/10 bg-slate-900/40 text-slate-300 hover:text-slate-100"
              }`}
            >
              All
            </button>
            {allTags.slice(0, 12).map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                data-testid={`kb-tag-${t}`}
                className={`rounded-full px-2 py-0.5 text-[11px] ${
                  tag === t
                    ? "bg-slate-100 text-slate-900"
                    : "border border-white/10 bg-slate-900/40 text-slate-300 hover:text-slate-100"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Generate-from-name modal */}
      {genOpen && (
        <div
          data-testid="kb-generate-modal"
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !genBusy && setGenOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-sky-500/30 bg-slate-950 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-sky-300">
              <Wand2 className="h-4 w-4" />
              <span className="text-[11px] uppercase tracking-[0.16em] font-semibold">
                AI · Claude Sonnet 4.5
              </span>
            </div>
            <h3 className="mt-1 text-xl font-bold text-slate-100">
              Generate {dbMeta.label.toLowerCase()} record
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Type a name or short description (e.g. <span className="text-slate-200">"DexaFit"</span>,{" "}
              <span className="text-slate-200">"Cerbo EHR"</span>, or{" "}
              <span className="text-slate-200">"GLP-1 injectable program"</span>) and Claude will pre-fill
              every field it has grounded knowledge for. Fields with no confident answer are left blank
              so you can verify rather than retype.
            </p>
            <div className="mt-4 space-y-3">
              <Input
                data-testid="kb-generate-input"
                autoFocus
                value={genHint}
                onChange={(e) => setGenHint(e.target.value)}
                placeholder="e.g. DexaFit, NovaPath Diagnostics, Hormone replacement program…"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !genBusy && genHint.trim()) runGenerate();
                }}
                className="bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500"
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={genBusy}
                  onClick={() => {
                    setGenOpen(false);
                    setGenHint("");
                  }}
                  className="bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="kb-generate-submit"
                  onClick={runGenerate}
                  disabled={genBusy || !genHint.trim()}
                >
                  {genBusy ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Claude is researching…
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                      Generate &amp; open
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="mt-3 text-[10.5px] text-slate-500">
              The generated draft is tagged <span className="text-slate-300">ai-generated</span>. Always
              verify — Claude is instructed not to invent contact info / URLs / compliance details, but
              its training data has a cutoff.
            </p>
          </div>
        </div>
      )}

      {/* AI Trends panel */}
      {aiOpen && (
        <section
          data-testid="kb-trends-panel"
          className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-sky-200">
              <Sparkles className="h-4 w-4" /> AI Trends — {dbMeta.label}
            </h3>
            <button
              onClick={() => setAiOpen(false)}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              close
            </button>
          </div>
          {aiLoading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" /> Claude is reading {records.length} records…
            </div>
          )}
          {aiTrends && (
            <div className="mt-3 space-y-3">
              {aiTrends.summary && (
                <p className="text-sm leading-6 text-slate-200">{aiTrends.summary}</p>
              )}
              <ul className="space-y-2">
                {(aiTrends.trends || []).map((t, i) => (
                  <li
                    key={`${t.title}-${i}`}
                    className="rounded-xl border border-white/10 bg-slate-900/40 p-3"
                  >
                    <div className="text-sm font-semibold text-slate-100">{t.title}</div>
                    {t.evidence?.length > 0 && (
                      <div className="mt-1 text-[11px] text-slate-400">
                        Evidence: {t.evidence.join(" · ")}
                      </div>
                    )}
                    {t.action && (
                      <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-sky-500/10 px-2 py-0.5 text-[11px] text-sky-200">
                        Action → {t.action}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Records list */}
      <section data-testid="kb-records-list">
        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-10 text-center text-sm text-slate-400">
            Loading…
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/40 p-10 text-center">
            <Brain className="mx-auto h-7 w-7 text-slate-500" />
            <p className="mt-2 text-sm text-slate-300">
              No records yet. Click "New record" to seed your first {dbMeta.label.toLowerCase()} entry.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {records.map((r) => {
              const title = r[dbMeta.primary] || "(untitled)";
              const status = r.program_status || r.approval_status || r.status || r.relationship_status || r.threat_level;
              const snippet =
                r.ai_summary ||
                r.program_description ||
                r.notes ||
                r.interview_notes ||
                r.summary ||
                "";
              return (
                <Link
                  key={r.id}
                  to={`/admin/knowledge-base/${dbKey}/${r.id}`}
                  data-testid={`kb-card-${r.id}`}
                  className={`group block rounded-2xl border border-white/10 bg-slate-950/60 p-5 transition ${cls.hoverBorder} hover:bg-slate-900/70`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-[10.5px] uppercase tracking-[0.16em] font-semibold ${cls.text}`}>
                        {r.category || r.specialty?.[0] || r.workflow_category || r.channel || dbMeta.label}
                      </div>
                      <h3 className="mt-1 line-clamp-2 text-base font-semibold text-slate-100 group-hover:text-white">
                        {title}
                      </h3>
                    </div>
                    {status && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${chipForStatus(status)}`}>
                        {status}
                      </span>
                    )}
                  </div>
                  {snippet && (
                    <p className="mt-2 line-clamp-3 text-xs text-slate-400">{snippet}</p>
                  )}
                  {r.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {r.tags.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="rounded-full border border-white/10 bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-slate-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  {(r.states_served || r.vendor_categories_required) && (
                    <div className="mt-2 text-[10.5px] text-slate-500 line-clamp-1">
                      {r.states_served ? `States: ${tagStr(r.states_served)}` : ""}
                      {r.vendor_categories_required
                        ? `Vendors needed: ${tagStr(r.vendor_categories_required)}`
                        : ""}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
